/**
 * Share Modal Component - Allow users to share articles via email
 * 
 * Author: Markus van Kempen - markus.van.kempen@gmail.com
 * Last Updated: November 2025
 * Version: 1.0.0
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { getIcon } from '../utils/icons';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './ShareModal.css';

const ShareModal = ({ 
  isOpen, 
  onClose, 
  contentTitle,     // Renamed from articleTitle for flexibility
  contentUrl,       // Renamed from articleUrl
  contentId,        // Renamed from pageId
  contentType = 'article',  // 'article' | 'tune' | 'tune-asset' | 'product'
  contentDescription = ''   // New: optional description
}) => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();
  const { t } = useLanguage();
  const ui = t.ui.share;
  const [recipientEmails, setRecipientEmails] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isLoggedIn = Boolean(currentUser && localStorage.getItem('token'));

  const handleCopyLink = async () => {
    if (!contentUrl) return;
    try {
      await navigator.clipboard.writeText(contentUrl);
      showSuccess(ui.linkCopied);
    } catch {
      showError(ui.copyFailed);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email addresses
    const emails = recipientEmails.split(',').map(email => email.trim()).filter(email => email);
    
    if (emails.length === 0) {
      showError(ui.errNoRecipients);
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter(email => !emailRegex.test(email));
    
    if (invalidEmails.length > 0) {
      showError(fmt(ui.errInvalidEmails, { list: invalidEmails.join(', ') }));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/content/share-content', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contentId,
          contentTitle,
          contentUrl,
          contentType,
          contentDescription,
          recipientEmails: emails,
          message: message.trim()
        })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        const failedCount = data.failed || 0;
        if (failedCount > 0 && data.succeeded > 0) {
          showSuccess(fmt(ui.sentPartial, { sent: data.succeeded, failed: failedCount }));
        } else {
          showSuccess(fmt(ui.sentAll, { count: emails.length }));
        }
        setRecipientEmails('');
        setMessage('');
        onClose();
      } else {
        showError(data.error || data.details?.failed?.[0]?.error || ui.errShareFailed);
      }
    } catch (error) {
      console.error(`Error sharing ${contentType}:`, error);
      showError(ui.errShareFailed);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRecipientEmails('');
      setMessage('');
      onClose();
    }
  };

  if (!isOpen) return null;

  // Get content type label for display
  const contentTypeLabel =
    contentType === 'product'
      ? ui.typeProduct
      : contentType === 'tune' || contentType === 'tune-asset'
        ? ui.typeTune
        : ui.typeArticle;
  const shareTypeLabel = fmt(ui.shareType, { type: contentTypeLabel });

  return (
    <div className="share-modal-overlay" onClick={handleClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>{getIcon('share')} {shareTypeLabel}</h2>
          <button 
            className="close-btn" 
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t.ui.common.close}
          >
            {getIcon('close')}
          </button>
        </div>

        <div className="share-modal-body">
          <div className="article-info">
            <h3>{contentTitle}</h3>
            {contentDescription && <p className="content-description">{contentDescription}</p>}
            <p className="article-url">{contentUrl}</p>
          </div>

          <div className="share-copy-row">
            <button
              type="button"
              className="btn btn-secondary share-copy-btn"
              onClick={handleCopyLink}
              disabled={isSubmitting}
            >
              {getIcon('link')} {ui.copyLink}
            </button>
          </div>

          {!isLoggedIn ? (
            <p className="share-login-hint">
              <Link to="/login">{ui.signIn}</Link> {ui.loginHint}
            </p>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="recipientEmails">
                {getIcon('mail')} {ui.recipients} *
              </label>
              <input
                type="text"
                id="recipientEmails"
                className="form-control"
                placeholder={ui.recipientsPlaceholder}
                value={recipientEmails}
                onChange={(e) => setRecipientEmails(e.target.value)}
                disabled={isSubmitting}
                required
              />
              <small className="form-help">
                {ui.recipientsHelp}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="message">
                {getIcon('edit')} {ui.personalMessage}
              </label>
              <textarea
                id="message"
                className="form-control"
                placeholder={ui.personalMessagePlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSubmitting}
                rows="4"
                maxLength="500"
              />
              <small className="form-help">
                {fmt(ui.charsUsed, { used: message.length })}
              </small>
            </div>

            <div className="share-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t.ui.common.cancel}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-small"></span> {t.ui.common.sending}
                  </>
                ) : (
                  <>
                    {getIcon('send')} {shareTypeLabel}
                  </>
                )}
              </button>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;

